/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement, ReactNode } from 'react';
import SortMenu from '@/components/product-listing/sort-menu';
import type { SortOption } from '@/lib/search/sort-options';
import { cn } from '@/lib/utils';

/**
 * BG's desktop results-column toolbar: the result count on the leading edge, the
 * borderless `Sort By  <value> ˅` control on the trailing edge, no box and no
 * rule of its own.
 *
 * ONE ROW, TWO SURFACES. Live BG renders the same toolbar on its PLP and its
 * SRP, and only the count COPY differs:
 *
 *   PLP  `7,668 Items`   — `.reference/live-shots/bg-plp-1280.png`, and the same
 *                          row with `7,667 Items` at `bg-plp-768.png`
 *   SRP  `3,726 Results` — `.reference/captures/bg-search-d-1280/App.tsx:2212`
 *                          and `bg-search-d-1024/App.tsx:256`, byte-identical
 *
 * The count is therefore a `ReactNode` prop rather than a `total` number: the
 * caller owns the wording, this component owns the row. That is the same split
 * `ProductListingHeader` uses for its sort slot, and it is why one component can
 * serve both surfaces without either one branching on the other's copy.
 *
 * DESKTOP ONLY (`hidden md:flex`), which is live's own `desktop-tablet-only` on
 * `div.product-list-info`. Below `md` BG's PLP shows the count under the
 * description (`BgPlpMasthead`) and its sort behind the bottom `Filters │ Sort`
 * pill (`BgFilterSortBar`) — two different elements in two different places, so
 * this row is not "the mobile row, hidden".
 *
 * GEOMETRY comes from the `--srp-sort-menu-row-*` tokens in
 * `theme/tokens/brand-bg/search.css` — `mt-[14px] mb-[20px]`, row colour
 * `rgb(101,101,101)`, read off BG's own captures at two desktop widths. Those
 * names say SRP because that is the surface they were measured on; the row is
 * the same element on both BG listing surfaces, so they are consumed rather than
 * duplicated under a PLP name. No value is re-measured here — exact spacing on
 * the PLP is phase-2 work.
 *
 * BOTH LISTING ROUTES MOUNT THIS. The category route passes `Items` copy and
 * the search route passes `Results` copy; neither inlines its own row any more.
 * The SRP used to (`routes/_app.search.tsx`, its `search-sort-row-bg` block) and
 * was collapsed onto this component 2026-08-22, measured byte-identical on both
 * surfaces at 390 / 768 / 1024 / 1280 / 1440 before and after — same class
 * attribute, same computed margins `14px` / `20px`, same `rgb(101,101,101)`, same
 * child order. The two copies had NOT drifted while they were separate; they
 * differed only in the two identity attributes below.
 *
 * WHY `countTestId` EXISTS. Consolidating had to leave the SRP's rendered
 * attributes untouched, and the SRP's count span has always been
 * `search-items-count` — four assertions in `routes/_app.search.test.tsx` name it,
 * and the NM arm of that same route reuses the id for its own count. So the id is
 * a caller-owned handle, exactly as the count copy is, and the default keeps the
 * PLP's own `bg-listing-toolbar-count` unchanged.
 */
export default function BgListingToolbar({
    count,
    serverSortOptions,
    fallbackSortOptions,
    testId,
    countTestId = 'bg-listing-toolbar-count',
    className,
}: {
    /** Rendered on the leading edge. The caller supplies the surface's own copy. */
    count: ReactNode;
    /** SCAPI's configured options; they win over `fallbackSortOptions` when present. */
    serverSortOptions?: Array<{ id?: string; label?: string }>;
    /** The brand's curated list — named by the caller, never defaulted (see lib/search/sort-options). */
    fallbackSortOptions: readonly SortOption[];
    testId?: string;
    /** The count span's handle. Defaults to this component's own; the SRP keeps `search-items-count`. */
    countTestId?: string;
    className?: string;
}): ReactElement {
    return (
        <div
            data-testid={testId}
            data-slot="bg-listing-toolbar"
            className={cn(
                'mt-(--srp-sort-menu-row-space-top) mb-(--srp-sort-menu-row-space-bottom) hidden items-center justify-between text-(--srp-sort-menu-row-color) md:flex',
                className
            )}>
            <span data-testid={countTestId}>{count}</span>
            <SortMenu serverOptions={serverSortOptions} fallbackOptions={fallbackSortOptions} />
        </div>
    );
}
